// Vector-profile primitives for the Auto-Formulate visualization.
//
// Real profile vectors come from the SSE `initial` and `result` events
// (cached `platform.profile_compound_cache` rows). Dimensionality varies
// per pin (typically 111 today). Values are vina-score-derived: negative
// = strong binding, positive = weak/repulsive. The heatmap renders the
// magnitude (|v|) on the existing green ramp so both signs read with
// equal visual weight.
//
// VEC_DIMS remains as a default for the few render fallbacks that still
// need a width hint (e.g., AIBanner's empty-target render), but
// downstream code should prefer the real `embeddingDim` from the SSE
// payload when available.

export const VEC_DIMS = 111;
const HEAT_MAX = 10;

// Deterministic hash-based per-id vector. Used as a visual placeholder in
// CompositionAside during AF Running — the food composition rows don't
// currently carry compound IDs from the server-side `composition` endpoint,
// so we can't look up real per-compound profiles for them. When the
// composition_api endpoint is extended to include `compoundId`, the aside
// can switch to `aiState.foodCompositionProfiles` and this fallback can
// be removed.
function fnv1a(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
export function vectorFor(id: string, family: string | null = null): number[] {
  const seed = (family || "neutral") + "|" + id;
  const familyShift = fnv1a(family || "") % 7;
  const out = new Array<number>(VEC_DIMS);
  for (let i = 0; i < VEC_DIMS; i++) {
    const h = fnv1a(seed + ":" + i);
    const f = (h % 100000) / 100000; // 0..1
    const bias = Math.sin((i / VEC_DIMS) * Math.PI * 2 + familyShift);
    const v = -7 + f * 17 + bias * 1.2;
    out[i] = Math.max(0, v);
  }
  return out;
}

// Color ramp: pale brand-cream → brand-green deep. Cells near zero fall
// back to the sunk background so empty cells read as hairline grid.
// Operates on |v| so negative-valued real profiles render correctly.
export function heatColor(v: number): string {
  const m = Math.abs(v);
  if (m <= 0.0001) return "var(--bg-sunk)";
  const t = Math.min(1, m / HEAT_MAX);
  const l = 96 - t * 76;
  const s = 12 + t * 30;
  return `hsl(140 ${s}% ${l}%)`;
}

// Grayscale variant — used for Target Profile rendering. Same |v| convention.
export function heatColorGray(v: number): string {
  const m = Math.abs(v);
  if (m <= 0.0001) return "var(--bg-sunk)";
  const t = Math.min(1, m / HEAT_MAX);
  const l = 94 - t * 78;
  return `hsl(120 4% ${l}%)`;
}

// Per-cell |target − current| → red-intensity gradient. Fades to transparent
// as the AI pulls the two profiles together; bright red where the gap is
// widest.
export function heatColorDelta(absDiff: number): string {
  if (absDiff <= 0.05) return "transparent";
  const t = Math.min(1, absDiff / 6);
  const l = 92 - t * 50;
  const s = 30 + t * 50;
  return `hsl(8 ${s}% ${l}%)`;
}

